import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Phone, Mail, MapPin, ChevronDown, ArrowRight, ShieldCheck, Check } from "lucide-react";
import { CONTACT } from "@/lib/brand";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_ebg-realty-pro/artifacts/4d2xj7vv_contact%20hero.png";
const MAP_IMG  = "https://customer-assets.emergentagent.com/job_ebg-realty-pro/artifacts/9m97uhho_map.png";

const CITY_OPTIONS = ["Mumbai", "Delhi NCR", "Bangalore", "Hyderabad", "Chandigarh", "Mohali", "Pune", "Dubai (UAE)"];
const LOOKING_FOR  = [
    "Buying a Property",
    "Renting a Property",
    "Investment Opportunity",
    "Selling my Property",
    "Channel Partner Onboarding",
    "Builder Partnership",
    "NRI Advisory",
    "General Inquiry",
];

// Map ?intent=... query param → the option we should pre-select in "I am looking for"
const INTENT_MAP = {
    sell:     "Selling my Property",
    buy:      "Buying a Property",
    rent:     "Renting a Property",
    invest:   "Investment Opportunity",
    nri:      "NRI Advisory",
    partner:  "Channel Partner Onboarding",
    builder:  "Builder Partnership",
};

export default function Contact() {
    const [params] = useSearchParams();
    const intent = params.get("intent");
    const service = params.get("service");
    const [form, setForm] = useState({
        name: "", phone: "", email: "", city: "",
        looking_for: INTENT_MAP[intent] || "",
        message: "", agree: false,
    });
    // Update the pre-selected intent if the URL changes (e.g., in-app nav).
    useEffect(() => {
        if (intent && INTENT_MAP[intent]) {
            setForm((f) => ({ ...f, looking_for: INTENT_MAP[intent] }));
        }
    }, [intent]);
    const [errors, setErrors] = useState({});
    const [busy, setBusy] = useState(false);

    const validate = (f) => {
        const e = {};
        if (!f.name.trim()) e.name = "Please share your full name.";
        else if (f.name.trim().length < 2) e.name = "Name looks too short.";
        if (!/^[+]?[0-9\s-]{7,15}$/.test(f.phone.trim())) e.phone = "Enter a valid phone number.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = "Enter a valid email address.";
        if (!f.agree) e.agree = "Please tick the consent box to continue.";
        return e;
    };

    const submit = async (e) => {
        e.preventDefault();
        const eObj = validate(form);
        setErrors(eObj);
        if (Object.keys(eObj).length > 0) {
            toast.error("Please fix the highlighted fields.");
            return;
        }
        setBusy(true);
        try {
            await api.post("/leads", {
                name: form.name, email: form.email, phone: form.phone,
                message: `${service ? `[${service}] ` : ""}[${form.looking_for || "General"}] [${form.city || "Any"}] ${form.message}`,
                source: "contact",
                service: service || undefined,
            });
            toast.success("Thank you. An EBG advisor will reach out shortly.");
            setForm({ name: "", phone: "", email: "", city: "", looking_for: INTENT_MAP[intent] || "", message: "", agree: false });
            setErrors({});
        } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
    };

    // Clear an individual error the moment the user edits that field.
    const updateField = (key, value) => {
        setForm((f) => ({ ...f, [key]: value }));
        if (errors[key]) setErrors((e) => { const n = { ...e }; delete n[key]; return n; });
    };

    return (
        <PageShell navVariant="dark">
            {/* ================= HERO ================= */}
            <section data-testid="contact-hero" className="relative bg-[#0a0d18] text-white overflow-hidden">
                {/* Right-half hero image */}
                <div className="absolute inset-y-0 right-0 w-full lg:w-1/2">
                    <img src={HERO_IMG} alt="EBG Realty Contact" className="w-full h-full object-cover"/>
                </div>
                {/* Left-to-right dark gradient fade */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#0a0d18] via-[#0a0d18]/95 lg:via-[#0a0d18]/85 to-transparent lg:to-[#0a0d18]/10"/>

                <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 pt-36 pb-20 md:pt-44 md:pb-28">
                    <div className="max-w-xl">
                        <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--ebg-gold)] mb-5" data-testid="contact-eyebrow">Contact Us</div>
                        <h1 className="font-serif text-4xl md:text-5xl lg:text-[64px] leading-[1.02]">
                            Let&rsquo;s start a<br/>conversation.<br/>
                            <em className="not-italic text-[var(--ebg-gold)]">We&rsquo;re here to help.</em>
                        </h1>
                        <p className="text-white/70 mt-8 leading-relaxed text-[15px] max-w-md">
                            Whether you&rsquo;re looking to buy, sell, invest, or need expert advice—our team is just a message away.
                        </p>
                    </div>
                </div>
            </section>

            {/* ================= FORM + OFFICES ================= */}
            <section data-testid="contact-form-offices" className="bg-[var(--ebg-ivory)]">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24 grid lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* Left: Form */}
                    <div className="lg:col-span-7">
                        <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--ebg-gold)]">Send Us a Message</div>
                        <h2 className="font-serif text-3xl md:text-4xl lg:text-[42px] mt-3 leading-[1.05]">We&rsquo;d love to hear from you.</h2>
                        <div className="w-14 h-px bg-[var(--ebg-gold)] mt-5"/>

                        <form onSubmit={submit} data-testid="contact-form" className="mt-8 space-y-4" noValidate>
                            <div className="grid md:grid-cols-2 gap-4">
                                <FieldWrap error={errors.name} testid="contact-name-error">
                                    <Input placeholder="Full Name *"     value={form.name}  onChange={(v) => updateField("name", v)}  required testid="contact-name" hasError={!!errors.name}/>
                                </FieldWrap>
                                <FieldWrap error={errors.phone} testid="contact-phone-error">
                                    <Input placeholder="Phone Number *"   value={form.phone} onChange={(v) => updateField("phone", v)} required testid="contact-phone" type="tel" hasError={!!errors.phone}/>
                                </FieldWrap>
                                <FieldWrap error={errors.email} testid="contact-email-error">
                                    <Input placeholder="Email Address *"  value={form.email} onChange={(v) => updateField("email", v)} required testid="contact-email" type="email" hasError={!!errors.email}/>
                                </FieldWrap>
                                <Select placeholder="Preferred City"  value={form.city}          onChange={(v) => setForm({...form, city: v})}         options={CITY_OPTIONS} testid="contact-city"/>
                            </div>
                            <Select placeholder="I am looking for"    value={form.looking_for}   onChange={(v) => setForm({...form, looking_for: v})}  options={LOOKING_FOR}  testid="contact-looking-for"/>
                            <textarea
                                placeholder="How can we help you?"
                                rows={5}
                                value={form.message}
                                onChange={(e) => setForm({...form, message: e.target.value})}
                                data-testid="contact-message"
                                className="w-full bg-white border border-black/10 px-4 py-3.5 text-[14px] outline-none focus:border-[var(--ebg-gold)] focus:ring-1 focus:ring-[var(--ebg-gold)]/20 resize-none"
                            />

                            <label className={`flex items-start gap-3 pt-2 cursor-pointer select-none ${errors.agree ? "text-red-700" : ""}`}>
                                <span className={`w-4 h-4 border flex items-center justify-center mt-0.5 shrink-0 transition-colors ${form.agree ? "bg-[var(--ebg-gold)] border-[var(--ebg-gold)]" : errors.agree ? "border-red-500" : "border-black/25"}`}>
                                    {form.agree && <Check size={11} className="text-black"/>}
                                </span>
                                <input type="checkbox" className="hidden" checked={form.agree} onChange={(e) => updateField("agree", e.target.checked)} data-testid="contact-agree"/>
                                <span className="text-[13px]">
                                    I agree to be contacted by EBG Realty and have read the{" "}
                                    <Link to="/privacy-policy" className="underline hover:text-[var(--ebg-gold)]">Privacy Policy</Link>.
                                </span>
                            </label>
                            {errors.agree && <div data-testid="contact-agree-error" className="text-[12px] text-red-700 -mt-2">{errors.agree}</div>}

                            <button
                                type="submit"
                                disabled={busy}
                                data-testid="contact-submit"
                                className="w-full max-w-md bg-[var(--ebg-gold)] text-black py-4 text-[11px] uppercase tracking-[0.32em] font-bold hover:bg-black hover:text-[var(--ebg-gold)] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-3"
                            >
                                {busy ? "Sending…" : (<>Book Consultation <ArrowRight size={14}/></>)}
                            </button>

                            <div className="flex items-start gap-2 text-[12px] text-black/55 pt-1">
                                <ShieldCheck size={14} className="text-[var(--ebg-gold)] shrink-0 mt-0.5"/>
                                <span>Your details stay with us — we never sell or share your data. Typical response time: under 2 business hours.</span>
                            </div>
                        </form>
                    </div>

                    {/* Right: Offices */}
                    <aside className="lg:col-span-5" data-testid="contact-offices">
                        <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--ebg-gold)] mb-8">Our Offices</div>

                        {/* India — Mumbai */}
                        <OfficeCard
                            region="India — Headquarters"
                            city="Mumbai, India"
                            addr={["Mumbai, India"]}
                            phone={CONTACT.display}
                            email="info@ebgrealty.in"
                            testid="office-mumbai"
                        />
                        <div className="h-px bg-black/10 my-8"/>

                        {/* General inquiries */}
                        <div data-testid="general-inquiries">
                            <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--ebg-gold)] mb-4">General Inquiries</div>
                            <a href="mailto:info@ebgrealty.in" className="inline-flex items-center gap-3 text-black hover:text-[var(--ebg-gold)] transition-colors">
                                <span className="w-8 h-8 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 flex items-center justify-center">
                                    <Mail size={13} className="text-[var(--ebg-gold)]"/>
                                </span>
                                <span className="text-[15px] font-medium">info@ebgrealty.in</span>
                            </a>
                        </div>
                    </aside>
                </div>
            </section>

            {/* ================= GLOBAL PRESENCE (dark) ================= */}
            <section data-testid="global-presence" className="relative bg-[#0a0d18] text-white overflow-hidden">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-20 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
                    {/* Copy */}
                    <div className="lg:col-span-5">
                        <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--ebg-gold)]">Our Presence</div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-[52px] mt-4 leading-[1.03]">
                            Rooted in Mumbai.<br/>
                            <em className="not-italic text-[var(--ebg-gold)]">Serving India &amp; beyond.</em>
                        </h2>
                        <p className="text-white/70 mt-6 leading-relaxed max-w-md text-[15px]">
                            Headquartered in Mumbai, our advisors serve buyers, builders and channel partners across every major Indian city — and NRIs worldwide.
                        </p>
                        <Link
                            to="/about"
                            data-testid="global-view-locations"
                            className="mt-10 inline-flex items-center gap-3 border border-[var(--ebg-gold)] text-[var(--ebg-gold)] px-7 py-3.5 text-[11px] uppercase tracking-[0.32em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black transition-colors"
                        >
                            View Locations <ArrowRight size={13}/>
                        </Link>
                    </div>

                    {/* Map */}
                    <div className="lg:col-span-7 relative" data-testid="world-map">
                        <img src={MAP_IMG} alt="EBG Realty — Global Presence: Mumbai & Dubai" className="w-full h-auto object-contain"/>
                    </div>
                </div>
            </section>
        </PageShell>
    );
}

/* ================= Sub-components ================= */

function Input({ placeholder, value, onChange, type = "text", required = false, testid, hasError = false }) {
    return (
        <input
            type={type}
            required={required}
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testid}
            aria-invalid={hasError || undefined}
            className={`w-full bg-white border px-4 py-3.5 text-[14px] outline-none focus:ring-1 placeholder:text-black/40 ${
                hasError
                    ? "border-red-500 focus:border-red-600 focus:ring-red-500/20"
                    : "border-black/10 focus:border-[var(--ebg-gold)] focus:ring-[var(--ebg-gold)]/20"
            }`}
        />
    );
}

function FieldWrap({ children, error, testid }) {
    return (
        <div>
            {children}
            {error && <div data-testid={testid} className="text-[12px] text-red-700 mt-1.5">{error}</div>}
        </div>
    );
}

function Select({ placeholder, value, onChange, options, testid }) {
    return (
        <div className="relative">
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                className={`appearance-none w-full bg-white border border-black/10 px-4 py-3.5 pr-10 text-[14px] outline-none focus:border-[var(--ebg-gold)] focus:ring-1 focus:ring-[var(--ebg-gold)]/20 cursor-pointer ${value ? "text-black" : "text-black/40"}`}
            >
                <option value="">{placeholder}</option>
                {options.map((o) => <option key={o} value={o} className="text-black">{o}</option>)}
            </select>
            <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-black/40 pointer-events-none"/>
        </div>
    );
}

function OfficeCard({ region, city, addr, phone, email, testid }) {
    return (
        <div data-testid={testid}>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]/85">{region}</div>
            <h3 className="font-serif text-3xl mt-2">{city}</h3>

            <div className="mt-5 space-y-3.5">
                <div className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 flex items-center justify-center shrink-0 mt-0.5">
                        <MapPin size={13} className="text-[var(--ebg-gold)]"/>
                    </span>
                    <div className="text-black/75 text-[14px] leading-relaxed">
                        {addr.map((line, i) => <div key={i}>{line}</div>)}
                    </div>
                </div>
                <a href={CONTACT.tel} className="flex items-center gap-3 text-black hover:text-[var(--ebg-gold)] transition-colors">
                    <span className="w-8 h-8 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 flex items-center justify-center shrink-0">
                        <Phone size={13} className="text-[var(--ebg-gold)]"/>
                    </span>
                    <span className="text-[14px] font-medium tabular-nums">{phone}</span>
                </a>
                <a href={`mailto:${email}`} className="flex items-center gap-3 text-black hover:text-[var(--ebg-gold)] transition-colors">
                    <span className="w-8 h-8 rounded-full bg-[var(--ebg-gold)]/10 border border-[var(--ebg-gold)]/40 flex items-center justify-center shrink-0">
                        <Mail size={13} className="text-[var(--ebg-gold)]"/>
                    </span>
                    <span className="text-[14px] font-medium">{email}</span>
                </a>
            </div>
        </div>
    );
}
